<template>
  <div
    v-if="!isAsking2FA"
    key="signin"
    class="prelogin-page w-full"
  >
    <h1
      v-if="hasRedirect"
      class="text-xl text-center mb-6"
    >
      Login to continue
    </h1>
    <p
      v-if="hasRedirect"
      class="text-sm text-center text-neutral-500 -mt-4 mb-6"
    >
      {{ `You will be signed in to ${$route.query.redirect} after you verify your identity.` }}
    </p>
    <h1
      v-if="!hasRedirect"
      class="text-xl text-center mb-6"
    >
      {{ $t('mycountly.signin.login') }}
    </h1>
    <div
      ref="googleLoginBtn"
      class="flex justify-center"
    />
    <div class="flex items-center gap-3 my-5">
      <div class="flex-1 h-px bg-[#EBEBEB]" />
      <span class="text-xs text-neutral-400 font-normal">or</span>
      <div class="flex-1 h-px bg-[#EBEBEB]" />
    </div>
    <cly-request
      disable-loading-icon
      silent-error
      @success="onSuccess"
      @error="onError"
    >
      <template #default="request">
        <cly-form>
          <template #default="v">
            <cly-form-step
              id="signupStep"
              @enter="handleEnter(request, v)"
            >
              <cly-form-field
                :rules="'required|max:320'"
                :label="$t('mycountly.signin.email')"
              >
                <el-input
                  v-model="signin.email"
                  data-test="signin-email"
                  type="text"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signin.placeholder.email')"
                />
              </cly-form-field>
              <cly-form-field
                vid="password"
                :link="{ url: '/#/forgot-password', text: 'Forgot password?' }"
                :rules="'required'"
                :label="$t('mycountly.signup.password')"
              >
                <el-input
                  v-model="signin.password"
                  data-test="signin-password"
                  type="password"
                  show-password
                  class="w-full mt-1"
                  tabindex="0"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-password')"
                />
              </cly-form-field>
              <myc-big-button
                test-id="signin-submit"
                type="success"
                :disabled="request.isPending || !v.isSubmissionAllowed"
                class="w-full !mt-5"
                @clicked="request.submit(getSigninSubmission)"
              >
                {{ $t('mycountly.signin.sign-in') }}
              </myc-big-button>
            </cly-form-step>
          </template>
        </cly-form>
      </template>
    </cly-request>
    <p class="text-center mt-6 text-neutral-500 text-[13px] font-normal">
      Don't have an account?{{ ' ' }}
      <router-link
        id="link__create-an-account"
        class="!text-[#1EAB56] hover:!text-[#178f47] !font-normal"
        to="/account/create"
      >
        Register
      </router-link>
    </p>
    <p
      v-if="isLocked"
      class="text-center mt-3 text-neutral-500 text-[13px]"
    >
      <router-link
        id="link__request-unlock-account"
        class="!text-[#1EAB56] hover:!text-[#178f47]"
        to="/request-unlock"
      >
        {{ $t('mycountly.signin.request-unlock-account') }}
      </router-link>
    </p>
  </div>

  <div
    v-else
    key="twofa"
    class="prelogin-page w-full"
  >
    <h1 class="text-xl text-center mb-4">
      {{ $t('mycountly.2fa.title') }}
    </h1>
    <p class="text-sm text-center text-neutral-500 mb-6">
      {{ $t('mycountly.2fa.please-enter-authcode') }}
    </p>
    <div class="flex justify-center">
      <verification-input
        v-model="signin.authCode"
        :length="6"
      />
    </div>
    <cly-request
      disable-loading-icon
      silent-error
      @success="onSuccess"
      @error="onError"
    >
      <template #default="request">
        <myc-big-button
          :disabled="signin.authCode.length < 6 || request.isPending"
          type="success"
          class="w-full !mt-5"
          @clicked="request.submit(getSigninSubmission)"
        >
          {{ $t('mycountly.signin.sign-in') }}
        </myc-big-button>
      </template>
    </cly-request>
    <p
      class="text-center mt-6 text-[13px] text-[#1EAB56] hover:text-[#178f47] cursor-pointer"
      @click="isAsking2FA = false"
    >
      Go back
    </p>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useAuthStore, useCompanyStore } from '@/stores';
import { GtmEvent, pushGtmEvent } from '@/utils/gtm';
import { PreloginNavigationMixin } from '@/utils/mixins/prelogin-navigation';
import { AnalyticsMixin } from '@/utils/mixins/analytics';
import { signInGoogle } from '@/modules/user/AuthService';
import ClyFormField from '@/components/countly/lib-components/form/cly-form-field.vue';
import ClyForm from '@/components/countly/lib-components/form/cly-form.vue';
import ClyFormStep from '@/components/countly/lib-components/form/cly-form-step.vue';
// import * as ProjectService from '@/modules/project/ProjectService';
import MycBigButton from '@/components/common/myc-big-button.vue';
import { generalAppVariables } from '@/constants';
import VerificationInput from '../common/containers/verification-input.vue';

export default {
  components: {
    MycBigButton,
    ClyForm,
    ClyFormStep,
    ClyFormField,
    VerificationInput,
  },
  mixins: [PreloginNavigationMixin, AnalyticsMixin],
  data() {
    return {
      signin: {
        email: '',
        password: '',
        authCode: '',
      },
      isAsking2FA: false,
      isLocked: false,
    };
  },
  computed: {
    ...mapState(useAuthStore, ['loggedInUser', 'isLoggedIn']),
    ...mapState(useCompanyStore, ['companies']),
    hasRedirect() {
      return !!this.$route.query.redirect;
    },
  },
  watch: {
    isAsking2FA(newVal) {
      if (newVal) {
        this.signin.authCode = '';
      }
    },
  },
  created() {
    this.tryResuming(this.$router, this.$route.query.redirect);

    if (this.$route.query.ref === 'unlocked') {
      this.$clyNotify({ message: 'Account unlocked', sticky: false, type: 'success' });
    }
  },
  mounted() {
    this.persistUtmTags();

    window.google.accounts.id.initialize({
      client_id: generalAppVariables.G_CLIENT_ID,
      callback: this.onCredentialsResponse,
      auto_select: true,
    });
    window.google.accounts.id.renderButton(this.$refs.googleLoginBtn, {
      text: 'continue_with', // or 'signup_with' | 'signin_with' | 'signin'
      size: 'large', // or 'small' | 'medium'
      width: '380', // max width 400
      theme: 'outline', // or 'filled_black' |  'filled_blue'
      logo_alignment: 'left', // or 'center'
    });

    if (this.$route.query.cly_id) {
      this.setDeviceId('landing', this.$route.query.cly_id);
    }
  },
  methods: {
    ...mapActions(useAuthStore, ['submitSignIn', 'refreshLoggedInUser']),
    persistUtmTags() {
      const utmTags = [];

      if (this.$route.query.utm_source) {
        utmTags.push({ key: 'utm_source', value: this.$route.query.utm_source });
      }

      if (this.$route.query.utm_medium) {
        utmTags.push({ key: 'utm_medium', value: this.$route.query.utm_medium });
      }

      if (this.$route.query.utm_campaign) {
        utmTags.push({ key: 'utm_campaign', value: this.$route.query.utm_campaign });
      }

      if (utmTags.length) {
        localStorage.setItem('utm_tags', JSON.stringify(utmTags));
      }
    },
    getSigninSubmission() {
      return this.submitSignIn(this.signin);
    },
    async onCredentialsResponse(response) {
      const signInGoogleResponse = await signInGoogle({ idToken: response.credential });

      if (signInGoogleResponse.error) {
        this.$clyNotify({ message: signInGoogleResponse.error, sticky: false, type: 'error' });
      } else {
        await this.refreshLoggedInUser();
        this.onSuccess(signInGoogleResponse.value?.payload?.justSignedUp);
      }
    },
    onSuccess(justSignedUp = false) {
      // send workspace informations to analytics server
      // if this isn't first time of user
      if (this.loggedInUser.onboarding !== 'company') {
        const company = this.companies.find((c) => c._id === this.loggedInUser.companies[0].company);
        const userProps = {
          name: this.loggedInUser.firstName,
          email: this.loggedInUser.email,
          custom: {
            Role: this.loggedInUser.designation,
          },
        };
        let workspaceProps;
        if (company) {
          userProps.organization = company.name;
          userProps.custom.TeamSize = company.size;
          workspaceProps = {
            name: company.name,
            organization: company.name,
            custom: {
              TeamSize: company.size,
            },
          };
          this.setDeviceId('workspace', company._id, false);
        }
        this.setUserProfile('user', userProps);
        this.setUserProfile('workspace', workspaceProps);
        this.setUserProfile('landing', userProps);
      } else {
        this.setUserProfile('user', {
          name: this.loggedInUser.firstName,
          email: this.loggedInUser.email,
        });
        this.setUserProfile('landing', {
          name: this.loggedInUser.firstName,
          email: this.loggedInUser.email,
        });
      }

      if (typeof justSignedUp === 'boolean' && justSignedUp) {
        this.eventSucceed('signupUser', {
          FirstName: this.loggedInUser.firstName, Name: this.loggedInUser.lastName, Email: this.loggedInUser.email, From: 'Signin',
        });
        pushGtmEvent(GtmEvent.ACCOUNT_CREATED, {
          account_id: this.loggedInUser._id,
          account_email: this.loggedInUser.email,
        });
      }

      this.eventSucceed('signIn', { FirstName: this.loggedInUser.firstName, LastName: this.loggedInUser.lastName, Email: this.loggedInUser.email });
      this.setDeviceId('user', this.loggedInUser.email, true);
      this.disableAnalytics('landing');

      if (this.$route.query.redirect) {
        this.$router.push({
          name: 'Redirect',
          query: {
            server_url: this.$route.query.redirect,
          },
        });

        return;
      }

      this.$router.push({
        name: 'Loading',
        query: {
          ...this.$route.query,
          referrer: 'login',
        },
      });
    },
    onError(error) {
      this.isAsking2FA = !!(error.payload && error.payload.failed2FA);
      this.isLocked = !!(error.payload && error.payload.locked);
      if (this.isAsking2FA) {
        if (error.payload.failed2FA !== 'required') {
          this.$clyNotify({ message: error.message, sticky: false, type: 'error' });
        }
      } else {
        this.$clyNotify({ message: error.message, sticky: false, type: 'error' });
      }
      this.eventFailed('signIn', error.message);
    },
    handleEnter(request, v) {
      if (request.pending || !v.isSubmissionAllowed) return;
      request.submit(this.getSigninSubmission);
    },
  },
};
</script>
